import { LABEL_SPECIFIC_PERCENTAGE, LABEL_SPECIFIC_COUNT, LABEL_GROUP, LABEL_INDIVIDUAL, LABEL_EQUAL_PERCENTAGES, LABEL_TYPE_WORK_PROJECT_COMPARE, LABEL_MULTI_USER } from "../../components/organisms/QARuleForm/constants";
import { AudienceConfig, OptionEntry, TaskConfig } from "../../types/cqa";
import { CQA_ASSIGN_LABEL_MAPPING, cqaAssignCoders, cqaAssignGroup, cqaDistributionMetadata } from "../api";

export const calculateTotalVolume = (users: OptionEntry[]) => {
    if (!users || users.length == 0) {
        return 0;
    }

    return users.reduce((acc, user) => acc + (user.volume || 0), 0);
}

export const calculateSpecificPercentageRemainingVolume = (users: OptionEntry[]): number => {
    const totalVolume = calculateTotalVolume(users);

    return 100 - totalVolume;
};

export const calculateSpecificCountRemainingVolume = (users: OptionEntry[], assignableCount: number): number => {
    if (!assignableCount) {
        return 0;
    }

    return assignableCount - calculateTotalVolume(users);
};

export const calculateRemainingVolume = (users: OptionEntry[], newVolumeType: string, unassignedCount: number): number => {
    const totalVolume = calculateTotalVolume(users);

    if (newVolumeType === LABEL_SPECIFIC_PERCENTAGE) {
        return 100 - totalVolume;
    }

    if (newVolumeType === LABEL_SPECIFIC_COUNT) {
        return unassignedCount - totalVolume;
    }

    return NaN;
};

export const validAssignedVolume = (individuals: OptionEntry[], volumeType: string, max: number): boolean => {
    const totalVolume = calculateTotalVolume(individuals);

    if (volumeType === LABEL_SPECIFIC_PERCENTAGE && (totalVolume >= 0 && totalVolume <= 100)) {
        return true;
    }

    if (volumeType === LABEL_SPECIFIC_COUNT && (totalVolume >= 0 && totalVolume <= max)) {
        return true;
    }

    return false;
}

export const validSubmittedVolume = (individuals: OptionEntry[], volumeType: string, unassignedCount: number): boolean => {
    const totalVolume = calculateTotalVolume(individuals);

    if (totalVolume === 0) {
        return true;
    }

    if (volumeType === LABEL_SPECIFIC_PERCENTAGE && totalVolume == 100) {
        return true;
    }

    if (volumeType === LABEL_SPECIFIC_COUNT && totalVolume == unassignedCount) {
        return true;
    }

    return false;
}

export const redistributeEqualVolume = (individuals: OptionEntry[]): void => {
    const count = individuals.length;
    const base = Math.floor(100 / count);
    const remainder = 100 % count;

    const arr = Array(count).fill(base);

    // distribute remainder to the last item(s)
    for (let i = 1; i <= remainder; i++) {
        arr[count - i] += 1;
    }

    individuals.forEach((ind, i) => {
        ind.volume = arr[i];
    });
}

export const processSubmittedAudience = async (cqaConfig: TaskConfig, projectId: string, groupId: string) => {
    const errors = [];
    const audienceConfig = cqaConfig.audience as AudienceConfig;
    const projectCompareMode = cqaConfig.typeOfWork === LABEL_TYPE_WORK_PROJECT_COMPARE;

    if (audienceConfig.distributionMethod === LABEL_INDIVIDUAL) {
        let volumeType = audienceConfig.distributionVolume === LABEL_EQUAL_PERCENTAGES
            ? CQA_ASSIGN_LABEL_MAPPING[LABEL_EQUAL_PERCENTAGES]
            : CQA_ASSIGN_LABEL_MAPPING[audienceConfig.volumeType];

        if (projectCompareMode) {
            volumeType = CQA_ASSIGN_LABEL_MAPPING[LABEL_MULTI_USER];
        }

        // Do nothing if there are no individuals
        // An empty array for projectCompareMode means users were initially set and then all deleted.
        // For now we will enforce projectCompareMode to have at least 1 user if assignments had happened.
        if (!audienceConfig.individuals || (audienceConfig.individuals.length == 0 && !projectCompareMode)) {
            return errors;
        }

        const metadata = await cqaDistributionMetadata(projectId).catch((err) => {
            errors.push("Error fetching distribution metadata");
            console.error(err);
        });

        if (!metadata) {
            return errors;
        }

        // Do nothing if nothing to assign
        if (metadata.assignable_count == 0) {
            return errors;
        }

        if (projectCompareMode) {
            if(audienceConfig.individuals.length == 0) {
                errors.push("Project compare mode requires at least 1 user")
                return errors;
            }
        }
        
        if (!projectCompareMode) {
            if (audienceConfig.volumeType && !validSubmittedVolume(audienceConfig.individuals, audienceConfig.volumeType, metadata.assignable_count)) {
                errors.push("There are remaining unassigned cases");
                return errors;
            }
        }

        await cqaAssignCoders(projectId, volumeType, audienceConfig.individuals).catch((err) => {
            errors.push("Error assinging cqa audience individuals");
            console.error(err);
        });
    }

    return errors;
};

export const isMultiUserMode = (assignessIds: string[], projectConfiguration) => {
    if (Array.isArray(assignessIds) && assignessIds.length > 1) {
        return true;
    }

    if (projectConfiguration?.typeOfWork === LABEL_TYPE_WORK_PROJECT_COMPARE) {
        return true;
    }

    return false;
}